import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatErr } from "@/lib/api";
import { useLang } from "@/i18n";
import { ShieldCheck, X, Send, Clock, FileCheck2, Ban, RotateCw, MessageSquare } from "lucide-react";

const fmtBytes = (b) => { const u=["B","KB","MB","GB","TB"]; let i=0,n=b||0; while(n>=1024&&i<u.length-1){n/=1024;i++} return `${n.toFixed(n>=100?0:1)} ${u[i]}`;};

export default function Admin() {
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [certs, setCerts] = useState([]);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [partnerText, setPartnerText] = useState("");

  const loadAll = async () => {
    try {
      const [s, u, t, cs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/transmissions"),
        api.get("/admin/certificates"),
      ]);
      setStats(s.data); setUsers(u.data); setTxs(t.data); setCerts(cs.data);
    } catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => { loadAll(); }, []);

  const approve = async (tid) => {
    setBusyId(tid); setErr("");
    try { await api.post(`/admin/transmissions/${tid}/approve`); await loadAll(); }
    catch (e) { setErr(formatErr(e)); } finally { setBusyId(null); }
  };
  const reject = async (tid) => {
    setBusyId(tid); setErr("");
    try { await api.post(`/admin/transmissions/${tid}/reject`); await loadAll(); }
    catch (e) { setErr(formatErr(e)); } finally { setBusyId(null); }
  };

  const regenCert = async (cid) => {
    setBusyId(cid); setErr("");
    try { await api.post(`/admin/certificates/${cid}/regenerate`); await loadAll(); }
    catch (e) { setErr(formatErr(e)); } finally { setBusyId(null); }
  };
  const revokeCert = async (cid) => {
    if (!window.confirm("Revoke this certificate? This cannot be undone without regeneration.")) return;
    setBusyId(cid); setErr("");
    try { await api.post(`/admin/certificates/${cid}/revoke`); await loadAll(); }
    catch (e) { setErr(formatErr(e)); } finally { setBusyId(null); }
  };
  const saveNotes = async () => {
    if (!noteFor) return;
    setBusyId(noteFor); setErr("");
    try {
      await api.post(`/admin/certificates/${noteFor}/notes`, {
        operator_notes: noteText,
        satellite_partner_confirmation: partnerText,
      });
      setNoteFor(null); setNoteText(""); setPartnerText("");
      await loadAll();
    } catch (e) { setErr(formatErr(e)); } finally { setBusyId(null); }
  };

  const pending = txs.filter((t) => t.payment_status === "pending_review");
  const recentTx = txs.slice(0, 30);

  return (
    <AppShell>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">Mission Operations</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="admin-title">Admin Panel</h1>
        </div>
        {pending.length > 0 && (
          <div className="holo-panel px-4 py-3 border-[#FFB800]/40 flex items-center gap-3">
            <Clock size={14} className="text-[#FFB800] blink" />
            <span className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-[#FFB800]" data-testid="admin-pending-count">
              {pending.length} {lang === "pt" ? "pendentes" : "awaiting release"}
            </span>
          </div>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
          {[
            ["Users", stats.users],
            ["Files", stats.files],
            ["Capsules", stats.capsules],
            ["Transmissions", stats.transmissions],
            ["Storage", fmtBytes(stats.storage_total_bytes)],
          ].map(([k, v], i) => (
            <div key={i} className="holo-panel p-5">
              <div className="label-overline">{k}</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-2">{v}</div>
            </div>
          ))}
        </div>
      )}

      {err && <div className="mb-4 text-[#FF3366] font-mono-data text-xs" data-testid="admin-error">{err}</div>}

      {/* Pending transmissions queue */}
      <div className="holo-panel p-6 mb-8" data-testid="admin-pending-panel">
        <div className="label-overline mb-4 flex items-center gap-2">
          <ShieldCheck size={12} /> {lang === "pt" ? "Liberação de Transmissões" : "Transmission Release Queue"}
        </div>
        {pending.length === 0 ? (
          <div className="text-white/40 text-sm" data-testid="admin-pending-empty">
            {lang === "pt" ? "Nenhuma transmissão aguardando liberação." : "No transmissions awaiting release."}
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-pending-list">
            {pending.map((t) => (
              <div key={t.transmission_id} className="border border-[#FFB800]/30 p-4" data-testid={`admin-pending-${t.transmission_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-data text-[11px] text-[#FFB800] tracking-[0.18em]">{t.transmission_id}</div>
                    <div className="font-display font-bold text-lg mt-1 tracking-tight truncate">{t.capsule_name}</div>
                    <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/55 mt-1">
                      → {t.destination?.name || "—"} · {t.destination?.distance_ly?.toFixed(2)} ly
                    </div>
                    <div className="font-mono-data text-[11px] text-white/65 mt-2">
                      {lang === "pt" ? "Método" : "Method"}: <span className="text-[#00D4FF]">{(t.payment_method || "").toUpperCase()}</span>
                      &nbsp; · &nbsp;{lang === "pt" ? "TX" : "TX"}: <span className="text-white/80">{t.payment_tx_ref || "—"}</span>
                    </div>
                    <div className="font-mono-data text-[10px] text-white/40 mt-1">
                      {lang === "pt" ? "Pacote" : "Package"}: {fmtBytes(t.package_size)} · {t.callsign}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(t.transmission_id)}
                      disabled={busyId === t.transmission_id}
                      data-testid={`approve-${t.transmission_id}`}
                      className="btn-primary-s2s !py-2 !px-4 !text-[11px]"
                    >
                      <ShieldCheck size={12} /> {lang === "pt" ? "LIBERAR" : "APPROVE"}
                    </button>
                    <button
                      onClick={() => reject(t.transmission_id)}
                      disabled={busyId === t.transmission_id}
                      data-testid={`reject-${t.transmission_id}`}
                      className="btn-secondary-s2s !py-2 !px-4 !text-[11px] border-[#FF3366]/40 hover:border-[#FF3366]"
                    >
                      <X size={12} /> {lang === "pt" ? "REJEITAR" : "REJECT"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent transmissions */}
      <div className="holo-panel p-6 mb-8">
        <div className="label-overline mb-4">{lang === "pt" ? "Transmissões Recentes" : "Recent Transmissions"}</div>
        <div className="overflow-auto">
          <table className="w-full font-mono-data text-[12px]">
            <thead className="text-[#00D4FF] tracking-[0.18em] uppercase">
              <tr>
                <th className="text-left py-2 pr-4">ID</th>
                <th className="text-left py-2 pr-4">Capsule</th>
                <th className="text-left py-2 pr-4">Destination</th>
                <th className="text-left py-2 pr-4">Payment</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {recentTx.map((t) => (
                <tr key={t.transmission_id} className="border-t border-white/5">
                  <td className="py-2 pr-4 text-[#00D4FF]">{t.transmission_id}</td>
                  <td className="py-2 pr-4 text-white truncate max-w-[200px]">{t.capsule_name}</td>
                  <td className="py-2 pr-4 text-white/70">{t.destination?.name || "—"}</td>
                  <td className="py-2 pr-4">
                    <span className={
                      t.payment_status === "approved" ? "text-[#62FFB0]" :
                      t.payment_status === "pending_review" ? "text-[#FFB800]" :
                      t.payment_status === "rejected" ? "text-[#FF3366]" : "text-white/55"
                    }>{t.payment_status || "unpaid"}</span>
                  </td>
                  <td className="py-2 pr-4 text-white/70">{(t.status || "").replace(/_/g, " ")}</td>
                  <td className="py-2 pr-4 text-white/50">{(t.created_at || t.started_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="holo-panel p-6 mb-6" data-testid="admin-certificates-panel">
        <div className="label-overline mb-4 flex items-center gap-2">
          <FileCheck2 size={12} /> Intergalactic Certificates · Certidões Intergalácticas
        </div>
        <div className="overflow-auto">
          <table className="w-full font-mono-data text-[12px]" data-testid="admin-certificates-table">
            <thead className="text-[#FFD27A] tracking-[0.18em] uppercase">
              <tr>
                <th className="text-left py-2 pr-4">Certificate ID</th>
                <th className="text-left py-2 pr-4">Mission</th>
                <th className="text-left py-2 pr-4">Destination</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-left py-2 pr-4">Notes</th>
                <th className="text-left py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {certs.slice(0, 30).map((c) => (
                <tr key={c.certificate_id} className="border-t border-white/5" data-testid={`cert-row-${c.certificate_id}`}>
                  <td className="py-2 pr-4 text-[#FFD27A]">{c.certificate_id}</td>
                  <td className="py-2 pr-4 text-white truncate max-w-[180px]">{c.capsule_name}</td>
                  <td className="py-2 pr-4 text-white/70">{c.destination_name || "—"}</td>
                  <td className="py-2 pr-4">
                    {c.revoked
                      ? <span className="text-[#FF6FA0]" data-testid={`cert-status-${c.certificate_id}`}>REVOKED</span>
                      : <span className="text-[#62FFB0]" data-testid={`cert-status-${c.certificate_id}`}>ACTIVE</span>}
                  </td>
                  <td className="py-2 pr-4 text-white/60 text-[11px] truncate max-w-[160px]">
                    {c.satellite_partner_confirmation || c.operator_notes || "—"}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex gap-2">
                      <a href={`/verify/cert/${c.certificate_id}`} target="_blank" rel="noreferrer"
                         className="text-[#00D4FF] hover:underline text-[10px] uppercase tracking-[0.16em]"
                         data-testid={`cert-view-${c.certificate_id}`}>VIEW</a>
                      <button onClick={() => regenCert(c.certificate_id)} disabled={busyId === c.certificate_id}
                              className="text-[#FFD27A] hover:underline text-[10px] uppercase tracking-[0.16em] disabled:opacity-40"
                              data-testid={`cert-regen-${c.certificate_id}`}>
                        <RotateCw size={10} className="inline" /> REGEN
                      </button>
                      <button onClick={() => { setNoteFor(c.certificate_id); setNoteText(c.operator_notes || ""); setPartnerText(c.satellite_partner_confirmation || ""); }}
                              className="text-[#A077FF] hover:underline text-[10px] uppercase tracking-[0.16em]"
                              data-testid={`cert-notes-${c.certificate_id}`}>
                        <MessageSquare size={10} className="inline" /> NOTES
                      </button>
                      {!c.revoked && (
                        <button onClick={() => revokeCert(c.certificate_id)} disabled={busyId === c.certificate_id}
                                className="text-[#FF6FA0] hover:underline text-[10px] uppercase tracking-[0.16em] disabled:opacity-40"
                                data-testid={`cert-revoke-${c.certificate_id}`}>
                          <Ban size={10} className="inline" /> REVOKE
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {noteFor && (
          <div className="mt-4 border border-[#A077FF]/40 p-4" data-testid="cert-notes-editor">
            <div className="label-overline text-[#A077FF]">Editing notes for {noteFor}</div>
            <div className="mt-3">
              <div className="label-overline text-[10px]">Satellite Partner Confirmation</div>
              <input value={partnerText} onChange={(e) => setPartnerText(e.target.value)}
                     className="w-full bg-black/40 border border-white/10 px-3 py-2 mt-1 text-sm font-mono-data"
                     placeholder="e.g. Amazon Kuiper Optical Relay · slot AK-2026-A14"
                     data-testid="cert-partner-input" />
            </div>
            <div className="mt-3">
              <div className="label-overline text-[10px]">Operator Notes</div>
              <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
                        rows={3}
                        className="w-full bg-black/40 border border-white/10 px-3 py-2 mt-1 text-sm"
                        placeholder="Internal operations notes…"
                        data-testid="cert-notes-input" />
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={saveNotes} disabled={busyId === noteFor}
                      className="btn-primary-s2s !text-[11px] disabled:opacity-40"
                      data-testid="cert-notes-save">SAVE NOTES</button>
              <button onClick={() => { setNoteFor(null); setNoteText(""); setPartnerText(""); }}
                      className="btn-secondary-s2s !text-[11px]"
                      data-testid="cert-notes-cancel">CANCEL</button>
            </div>
          </div>
        )}
      </div>

      <div className="holo-panel p-6">
        <div className="label-overline mb-4">Operators</div>
        <div className="overflow-auto">
          <table className="w-full font-mono-data text-[12px]" data-testid="admin-users-table">
            <thead className="text-[#00D4FF] tracking-[0.18em] uppercase">
              <tr>
                <th className="text-left py-2 pr-4">Name</th>
                <th className="text-left py-2 pr-4">Email</th>
                <th className="text-left py-2 pr-4">Role</th>
                <th className="text-left py-2 pr-4">Plan</th>
                <th className="text-left py-2 pr-4">Storage</th>
                <th className="text-left py-2 pr-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-white/5">
                  <td className="py-2 pr-4 text-white">{u.name}</td>
                  <td className="py-2 pr-4 text-white/80">{u.email}</td>
                  <td className="py-2 pr-4 text-white/55">{u.role}</td>
                  <td className="py-2 pr-4 text-white/55">{u.plan_id || "—"}</td>
                  <td className="py-2 pr-4 text-white/55">{fmtBytes(u.storage_used)}</td>
                  <td className="py-2 pr-4 text-white/55">{(u.created_at || "").slice(0, 19)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
